import { useQuery } from "@apollo/client/react";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import { createDefaultMutationHook } from "hooks/useMutationHandler";
import useTypedContext from "hooks/useTypedContext";

import {
  GET_PERSONAL_API_KEYS_COUNT,
  GET_PERSONAL_API_KEYS_ENABLED,
  REQUEST_PERSONAL_API_KEYS_ENABLING,
  SET_PERSONAL_API_KEYS_ENABLED,
} from "./gql";

const useSetPersonalApiKeysEnabledMutation = createDefaultMutationHook(
  SET_PERSONAL_API_KEYS_ENABLED
);
const useRequestPersonalApiKeysEnablingMutation = createDefaultMutationHook(
  REQUEST_PERSONAL_API_KEYS_ENABLING
);

const usePersonalApiKeysState = () => {
  const { reportSuccess } = useTypedContext(FlashContext);

  const { data, loading: isLoading } = useQuery(GET_PERSONAL_API_KEYS_ENABLED);

  const [setPersonalApiKeysEnabled, { loading: isSaving }] = useSetPersonalApiKeysEnabledMutation({
    refetchQueries: [
      GET_PERSONAL_API_KEYS_ENABLED,
      GET_PERSONAL_API_KEYS_COUNT,
      "GetPersonalApiKeys",
    ],
  });
  const [requestPersonalApiKeysEnabling] = useRequestPersonalApiKeysEnablingMutation();

  const setEnabled = useCallback(
    async (enabled: boolean, disableExistingKeys = false) => {
      const result = await setPersonalApiKeysEnabled({ enabled, disableExistingKeys }, (data) => {
        if (data) {
          reportSuccess({ message: `Personal API keys ${enabled ? "enabled" : "disabled"}` });
        }
      });

      return !!result?.data;
    },
    [setPersonalApiKeysEnabled, reportSuccess]
  );

  const requestEnable = useCallback(() => {
    requestPersonalApiKeysEnabling({}, (data) => {
      if (data) {
        reportSuccess({ message: "Request sent to your account admins" });
      }
    });
  }, [requestPersonalApiKeysEnabling, reportSuccess]);

  return {
    enabled: data?.personalApiKeysEnabled,
    isLoading,
    isSaving,
    setEnabled,
    requestEnable,
  };
};

export default usePersonalApiKeysState;
